import { Navigate, Route, Routes } from "react-router-dom";

import AppShell from "./layout/AppShell";
import PublicSiteLayout from "./layout/PublicSiteLayout";
import ProtectedRoute from "../auth/ProtectedRoute";
import PublicOnlyRoute from "../auth/PublicOnlyRoute";
import RoleProtectedRoute from "../auth/RoleProtectedRoute";
import ProviderOnboardingRoute, {
  ProviderPostLoginRedirect,
} from "../auth/ProviderOnboardingRoute";

import HomePage from "../pages/HomePage";
import AboutPage from "../pages/AboutPage";
import ServicesPage from "../pages/ServicesPage";
import FindProviderPage from "../pages/FindProviderPage";
import AiPlansPage from "../pages/AiPlansPage";
import ProviderPlansPage from "../pages/ProviderPlansPage";
import CareersPage from "../pages/CareersPage";
import TeamPage from "../pages/TeamPage";
import DownloadsPage from "../pages/DownloadsPage";
import ContactPage from "../pages/ContactPage";
import DashboardHomePage from "../pages/DashboardHomePage";
import NotFoundPage from "../pages/NotFoundPage";

import LoginPage from "../features/auth/pages/LoginPage";
import SignupPage from "../features/auth/pages/SignupPage";
import ForgotPasswordPage from "../features/auth/pages/ForgotPasswordPage";
import ResetPasswordPage from "../features/auth/pages/ResetPasswordPage";
import AuthCallbackPage from "../features/auth/pages/AuthCallbackPage";
import ProviderLoginPage from "../features/auth/pages/ProviderLoginPage";
import ProviderSignupPage from "../features/auth/pages/ProviderSignupPage";
import StaffLoginPage from "../features/auth/pages/StaffLoginPage";
import StaffResetPasswordPage from "../features/auth/pages/StaffResetPasswordPage";

import ProvidersPage from "../features/providers/pages/ProvidersPage";
import ProviderDetailPage from "../features/providers/pages/ProviderDetailPage";
import PlatformSettingsPage from "../features/platform-settings/pages/PlatformSettingsPage";
import ServiceCategoriesPage from "../features/services/pages/ServiceCategoriesPage";
import StaffPage from "../features/staff/pages/StaffPage";
import AiPlansAdminPage from "../features/ai/pages/AiPlansAdminPage";
import ProviderPlansAdminPage from "../features/provider-subscriptions/pages/ProviderPlansAdminPage";

import CustomerDashboardPage from "../features/customer/pages/CustomerDashboardPage";
import CustomerBookingsPage from "../features/bookings/pages/CustomerBookingsPage";

import OnboardingShell from "../features/provider-owner/onboarding/components/OnboardingShell";
import ProviderDashboardLayout from "../features/provider-owner/layout/ProviderDashboardLayout";

import ProviderOnboardingStartPage from "../features/provider-owner/onboarding/pages/ProviderOnboardingStartPage";
import ProviderProfileStepPage from "../features/provider-owner/onboarding/pages/ProviderProfileStepPage";
import ProviderVerificationStepPage from "../features/provider-owner/onboarding/pages/ProviderVerificationStepPage";
import ProviderServicesStepPage from "../features/provider-owner/onboarding/pages/ProviderServicesStepPage";
import ProviderCalendarStepPage from "../features/provider-owner/onboarding/pages/ProviderCalendarStepPage";
import ProviderDepositPolicyStepPage from "../features/provider-owner/onboarding/pages/ProviderDepositPolicyStepPage";
import ProviderPortfolioStepPage from "../features/provider-owner/onboarding/pages/ProviderPortfolioStepPage";
import ProviderReviewSubmitPage from "../features/provider-owner/onboarding/pages/ProviderReviewSubmitPage";
import ProviderOnboardingCompletePage from "../features/provider-owner/onboarding/pages/ProviderOnboardingCompletePage";

import ProviderDashboardHomePage from "../features/provider-owner/dashboard/pages/ProviderDashboardHomePage";
import ProviderProfilePage from "../features/provider-owner/dashboard/pages/ProviderProfilePage";
import ProviderVerificationPage from "../features/provider-owner/dashboard/pages/ProviderVerificationPage";
import ProviderStylistsPage from "../features/provider-owner/dashboard/pages/ProviderStylistsPage";
import ProviderNewStylistPage from "../features/provider-owner/dashboard/pages/ProviderNewStylistPage";
import ProviderStylistDetailPage from "../features/provider-owner/dashboard/pages/ProviderStylistDetailPage";
import ProviderServicesPage from "../features/provider-owner/dashboard/pages/ProviderServicesPage";
import ProviderNewServicePage from "../features/provider-owner/dashboard/pages/ProviderNewServicePage";
import ProviderServiceDetailPage from "../features/provider-owner/dashboard/pages/ProviderServiceDetailPage";
import ProviderCalendarPage from "../features/provider-owner/dashboard/pages/ProviderCalendarPage";
import ProviderBookingsPage from "../features/provider-owner/dashboard/pages/ProviderBookingsPage";
import ProviderBookingDetailPage from "../features/provider-owner/dashboard/pages/ProviderBookingDetailPage";
import ProviderFinancePage from "../features/provider-owner/dashboard/pages/ProviderFinancePage";
import ProviderPaymentPoliciesPage from "../features/provider-owner/dashboard/pages/ProviderPaymentPoliciesPage";
import ProviderPortfolioPage from "../features/provider-owner/dashboard/pages/ProviderPortfolioPage";
import ProviderReviewsPage from "../features/provider-owner/dashboard/pages/ProviderReviewsPage";
import ProviderSubscriptionPage from "../features/provider-owner/dashboard/pages/ProviderSubscriptionPage";

import MdDashboardPage from "../features/staff/pages/MdDashboardPage";
import HrDashboardPage from "../features/staff/pages/HrDashboardPage";
import HrManagerWorkbenchPage from "../features/staff/pages/HrManagerWorkbenchPage";
import HrEmployeeOnboardingPage from "../features/staff/pages/HrEmployeeOnboardingPage";
import OperationsDashboardPage from "../features/staff/pages/OperationsDashboardPage";
import AuditDashboardPage from "../features/staff/pages/AuditDashboardPage";
import FinanceDashboardPage from "../features/staff/pages/FinanceDashboardPage";
import ItDashboardPage from "../features/staff/pages/ItDashboardPage";
import GrievanceDashboardPage from "../features/staff/pages/GrievanceDashboardPage";
import CfoDashboardPage from "../features/staff/pages/CfoDashboardPage";
import AccountsDashboardPage from "../features/staff/pages/AccountsDashboardPage";

import { useAuth } from "../hooks/useAuth";
import { getStaffDashboardPath } from "../auth/roleRoutes";

function StaffDashboardIndexRedirect() {
  const { user } = useAuth();
  return <Navigate to={getStaffDashboardPath(user?.staffSubRole)} replace />;
}

export function RouterProviderApp() {
  return (
    <Routes>
      <Route element={<PublicSiteLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<Navigate to="/" replace />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/find-provider" element={<FindProviderPage />} />
        <Route path="/ai-plans" element={<AiPlansPage />} />
        <Route path="/provider-plans" element={<ProviderPlansPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/downloads" element={<DownloadsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        <Route
          path="/login"
          element={
            <PublicOnlyRoute>
              <LoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnlyRoute>
              <SignupPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnlyRoute>
              <ForgotPasswordPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/provider/login"
          element={
            <PublicOnlyRoute>
              <ProviderLoginPage />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/provider/signup"
          element={
            <PublicOnlyRoute>
              <ProviderSignupPage />
            </PublicOnlyRoute>
          }
        />

        <Route path="/staff/login" element={<StaffLoginPage />} />

        <Route
          path="/staff/reset-password"
          element={
            <ProtectedRoute redirectTo="/staff/login">
              <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
                <StaffResetPasswordPage />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
      </Route>

      <Route
        path="/dashboard"
        element={<Navigate to="/staff/dashboard" replace />}
      />
      <Route
        path="/hnb-staff/dashboard"
        element={<Navigate to="/staff/dashboard" replace />}
      />

      <Route
        path="/customer/dashboard"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["customer"]}>
              <CustomerDashboardPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customer/bookings"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["customer"]}>
              <CustomerBookingsPage />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/staff/dashboard"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <AppShell />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<StaffDashboardIndexRedirect />} />
        <Route path="overview" element={<DashboardHomePage />} />

        <Route
          path="providers"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <ProvidersPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="providers/:id"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <ProviderDetailPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="platform-settings"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <PlatformSettingsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="services/categories"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <ServiceCategoriesPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="staff"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <StaffPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="ai/plans"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <AiPlansAdminPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="provider-plans"
          element={
            <RoleProtectedRoute allowedRoles={["hnb_staff"]}>
              <ProviderPlansAdminPage />
            </RoleProtectedRoute>
          }
        />

        <Route
          path="md"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO"]}
            >
              <MdDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="hr"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "HR_HEAD", "HR_MANAGER"]}
            >
              <HrDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="hr/workbench"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "HR_HEAD", "HR_MANAGER"]}
            >
              <HrManagerWorkbenchPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="hr/onboarding"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "HR_HEAD", "HR_MANAGER"]}
            >
              <HrEmployeeOnboardingPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="operations"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={[
                "CEO",
                "BUSINESS_HEAD",
                "BDO",
                "OPERATIONS",
              ]}
            >
              <OperationsDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="audit"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "AUDIT_HEAD", "AUDITOR"]}
            >
              <AuditDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="finance"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "FINANCE_HEAD", "FINANCE_OFFICER"]}
            >
              <FinanceDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="it"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={[
                "CEO",
                "CTO",
                "ERP_HEAD",
                "LLM_HEAD",
                "IT_COMPLIANCE_HEAD",
              ]}
            >
              <ItDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="grievance"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={[
                "CEO",
                "GRIEVANCE_HEAD",
                "GRIEVANCE_OFFICER",
                "SUPPORT",
              ]}
            >
              <GrievanceDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="cfo"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "CFO"]}
            >
              <CfoDashboardPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="accounts"
          element={
            <RoleProtectedRoute
              allowedRoles={["hnb_staff"]}
              allowedStaffSubRoles={["CEO", "ACCOUNTS_HEAD", "ACCOUNTS_OFFICER"]}
            >
              <AccountsDashboardPage />
            </RoleProtectedRoute>
          }
        />
      </Route>

      <Route
        path="/provider/entry"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["provider_owner"]}>
              <ProviderPostLoginRedirect />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/provider/onboarding"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["provider_owner"]}>
              <ProviderOnboardingRoute>
                <OnboardingShell />
              </ProviderOnboardingRoute>
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="start" replace />} />
        <Route path="start" element={<ProviderOnboardingStartPage />} />
        <Route path="profile" element={<ProviderProfileStepPage />} />
        <Route path="verification" element={<ProviderVerificationStepPage />} />
        <Route path="services" element={<ProviderServicesStepPage />} />
        <Route path="calendar" element={<ProviderCalendarStepPage />} />
        <Route
          path="deposit-policy"
          element={<ProviderDepositPolicyStepPage />}
        />
        <Route path="portfolio" element={<ProviderPortfolioStepPage />} />
        <Route path="review" element={<ProviderReviewSubmitPage />} />
        <Route path="complete" element={<ProviderOnboardingCompletePage />} />
      </Route>

      <Route
        path="/provider/dashboard"
        element={
          <ProtectedRoute>
            <RoleProtectedRoute allowedRoles={["provider_owner"]}>
              <ProviderDashboardLayout />
            </RoleProtectedRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<ProviderDashboardHomePage />} />
        <Route path="profile" element={<ProviderProfilePage />} />
        <Route path="verification" element={<ProviderVerificationPage />} />
        <Route path="stylists" element={<ProviderStylistsPage />} />
        <Route path="stylists/new" element={<ProviderNewStylistPage />} />
        <Route
          path="stylists/:stylistId"
          element={<ProviderStylistDetailPage />}
        />
        <Route path="services" element={<ProviderServicesPage />} />
        <Route path="services/new" element={<ProviderNewServicePage />} />
        <Route
          path="services/:serviceId"
          element={<ProviderServiceDetailPage />}
        />
        <Route path="calendar" element={<ProviderCalendarPage />} />
        <Route path="bookings" element={<ProviderBookingsPage />} />
        <Route
          path="bookings/:bookingId"
          element={<ProviderBookingDetailPage />}
        />
        <Route path="finance" element={<ProviderFinancePage />} />
        <Route
          path="finance/policies"
          element={<ProviderPaymentPoliciesPage />}
        />
        <Route path="portfolio" element={<ProviderPortfolioPage />} />
        <Route path="reviews" element={<ProviderReviewsPage />} />
        <Route path="subscription" element={<ProviderSubscriptionPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}